import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { Check, Link2, Loader2, Search, Trash2, X } from 'lucide-react';
import { saveDocPage } from '../../../services/api';

const DIR_API = '/decoupled-api/taxonomy_term/doc_directory';
const TASKS_API = '/decoupled-api/node/task/collection?display=decoupled';
const DEBOUNCE_MS = 300;

async function fetchJson(url) {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
}

function parseTask(item) {
    const d = item.data ?? item;
    return {
        id: parseInt(d.id, 10),
        title: d.title || `Tâche #${d.id}`,
        status: d.status || d.moderationState || '',
        projectId: d.projectId ? parseInt(d.projectId, 10) : null,
    };
}

export const DocTaskLinker = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const pageId = evaluateTemplateValue({
        valueToEvaluate: props.pageId,
        templateContext,
        globalDataContext,
    });

    const rootDirectoryId = evaluateTemplateValue({
        valueToEvaluate: props.rootDirectoryId,
        templateContext,
        globalDataContext,
    });

    const linkedTaskIdsRaw = evaluateTemplateValue({
        valueToEvaluate: props.linkedTasks,
        templateContext,
        globalDataContext,
    }) || [];

    const dataLocationPath = props.dataLocationPath || '';

    const [projectId, setProjectId] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [linkedDetails, setLinkedDetails] = useState([]);
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [resolving, setResolving] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [savedOk, setSavedOk] = useState(false);
    const debounceRef = useRef(null);
    const savedTimerRef = useRef(null);
    const wrapperRef = useRef(null);

    const linkedIdsKey = JSON.stringify(linkedTaskIdsRaw);
    const currentLinkedIds = useMemo(() => {
        if (!Array.isArray(linkedTaskIdsRaw)) return [];
        return linkedTaskIdsRaw.map((id) => parseInt(id, 10)).filter(Boolean);
    }, [linkedIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!rootDirectoryId) { setResolving(false); return; }
        setResolving(true);
        fetchJson(`${DIR_API}/${rootDirectoryId}?display=decoupled`)
            .then((data) => {
                const d = data?.data ?? data;
                const pid = d?.projectId ? parseInt(d.projectId, 10) : null;
                setProjectId(pid);
            })
            .catch(() => setProjectId(null))
            .finally(() => setResolving(false));
    }, [rootDirectoryId]);

    useEffect(() => {
        if (!projectId) return;
        fetchJson(`${TASKS_API}&filter[0][f]=field_project&filter[0][v]=${projectId}&sort[0][f]=changed&sort[0][d]=DESC&range=200`)
            .then((data) => {
                const items = (data || []).filter(Boolean).map(parseTask);
                setTasks(items);
            })
            .catch(() => setTasks([]));
    }, [projectId]);

    useEffect(() => {
        if (currentLinkedIds.length === 0) { setLinkedDetails([]); return; }
        const found = currentLinkedIds.map((id) => {
            const fromCache = tasks.find((t) => t.id === id);
            if (fromCache) return fromCache;
            return { id, title: `Tâche #${id}`, status: '' };
        });
        setLinkedDetails(found);
    }, [currentLinkedIds, tasks]);

    const doSearch = useCallback((term) => {
        if (!term.trim()) { setSearchResults(null); return; }
        const q = term.toLowerCase();
        const filtered = tasks
            .filter((t) => t.title.toLowerCase().includes(q) || String(t.id).includes(q))
            .filter((t) => !currentLinkedIds.includes(t.id))
            .slice(0, 15);
        setSearchResults(filtered);
    }, [tasks, currentLinkedIds]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query.trim()) { setSearchResults(null); return; }
        debounceRef.current = setTimeout(() => doSearch(query), DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
    }, [query, doSearch]);

    const persistLinks = useCallback(async (updated) => {
        if (!pageId) return;
        setSaving(true);
        setSaveError(null);
        setSavedOk(false);
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        try {
            await saveDocPage({ id: pageId, relatedTasks: updated });
            setSavedOk(true);
            savedTimerRef.current = setTimeout(() => setSavedOk(false), 2000);
            window.dispatchEvent(new CustomEvent('doc:tasks-updated'));
        } catch (err) {
            setSaveError(err?.message || 'Erreur de sauvegarde');
        } finally {
            setSaving(false);
        }
    }, [pageId]);

    const handleAdd = useCallback(async (taskId) => {
        const updated = [...currentLinkedIds, taskId];
        if (dataLocationPath) {
            globalDataContext.updateData(updated, dataLocationPath);
        }
        setQuery('');
        setSearchResults(null);
        await persistLinks(updated);
    }, [currentLinkedIds, dataLocationPath, globalDataContext, persistLinks]);

    const handleRemove = useCallback(async (taskId) => {
        const updated = currentLinkedIds.filter((id) => id !== taskId);
        if (dataLocationPath) {
            globalDataContext.updateData(updated, dataLocationPath);
        }
        await persistLinks(updated);
    }, [currentLinkedIds, dataLocationPath, globalDataContext, persistLinks]);

    useEffect(() => {
        if (!showSearch) return;
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSearch(false);
                setQuery('');
                setSearchResults(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showSearch]);

    useEffect(() => () => {
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    }, []);

    if (resolving) {
        return (
            <ActionDependant {...props}>
                <div className="flex items-center gap-2 text-xs text-stone-400 py-2">
                    <Loader2 size={12} className="animate-spin" />
                    Chargement…
                </div>
            </ActionDependant>
        );
    }

    if (!projectId) {
        return (
            <ActionDependant {...props}>
                <p className="text-xs text-stone-400 italic py-2">
                    Aucun projet lié à cet espace.
                </p>
            </ActionDependant>
        );
    }

    return (
        <ActionDependant {...props}>
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest">
                            Tâches liées
                        </h3>
                        {saving && (
                            <Loader2 size={11} className="animate-spin text-stone-400" aria-label="Enregistrement…" />
                        )}
                        {savedOk && !saving && (
                            <Check size={11} className="text-emerald-500" aria-label="Enregistré" />
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowSearch(!showSearch)}
                        disabled={saving}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {showSearch ? 'Fermer' : '+ Lier une tâche'}
                    </button>
                </div>

                {saveError && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                        <X size={11} />
                        {saveError}
                    </p>
                )}

                {showSearch && (
                    <div ref={wrapperRef} className="relative">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Rechercher une tâche…"
                                autoFocus
                                aria-label="Rechercher une tâche"
                                className="w-full pl-9 pr-8 py-2 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                            />
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => { setQuery(''); setSearchResults(null); }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-stone-400 hover:text-stone-600"
                                    aria-label="Effacer la recherche"
                                >
                                    <X size={12} />
                                </button>
                            )}
                        </div>

                        {searchResults !== null && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                                {searchResults.length === 0 ? (
                                    <div className="px-3 py-4 text-center text-xs text-stone-400">
                                        Aucune tâche trouvée.
                                    </div>
                                ) : (
                                    searchResults.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => handleAdd(t.id)}
                                            className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-stone-50 transition-colors"
                                        >
                                            <Link2 size={12} className="text-stone-400 flex-shrink-0" />
                                            <span className="truncate flex-1 text-stone-700">{t.title}</span>
                                            <span className="text-3xs text-stone-400 flex-shrink-0">#{t.id}</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                )}

                {linkedDetails.length > 0 ? (
                    <div className="space-y-1">
                        {linkedDetails.map((t) => (
                            <div
                                key={t.id}
                                className="flex items-center gap-2 px-3 py-2 bg-stone-50 rounded-lg group"
                            >
                                <Link2 size={12} className="text-stone-400 flex-shrink-0" />
                                <a
                                    href={`/tasks/${t.id}`}
                                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline truncate flex-1"
                                >
                                    {t.title}
                                </a>
                                <span className="text-3xs text-stone-400 flex-shrink-0">#{t.id}</span>
                                <button
                                    type="button"
                                    onClick={() => handleRemove(t.id)}
                                    disabled={saving}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 text-stone-400 hover:text-red-500 transition-all flex-shrink-0 disabled:opacity-20"
                                    title="Retirer"
                                    aria-label="Retirer la tâche"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-stone-400 italic">Aucune tâche liée.</p>
                )}
            </div>
        </ActionDependant>
    );
};
